import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  Response,
  SelectItemProps,
  PermissionEnum,
  Currency,
  AccountingJobProps as AccountingJob,
} from '@ui/src';
import { reportsDictionariesApi, PermissionResponse } from './service';
import { Category } from '../../hoc/types';

export interface ReportsDictionariesState {
  currencies?: SelectItemProps[];
  activePermissions?: PermissionEnum[];
  baseCurrency?: Currency;
  currenciesList?: Currency[];
  whitelabel?: {
    data: {
      value: Record<string, string>;
    };
  };
  accountsCategories?: Category[];
  accountingJobs?: AccountingJob[];
}

const initialState: ReportsDictionariesState = {};

export const reportsDictionariesSlice = createSlice({
  name: 'reportsDictionaries',
  initialState,
  reducers: {
    setActivePermissions: (state, action: PayloadAction<PermissionEnum[]>) => {
      state.activePermissions = action.payload;
    },
    setReportsCurrencies: (state, action: PayloadAction<Currency[]>) => {
      state.currencies = action.payload.map((item) => ({
        name: item.name,
        value: item.id,
      }));

      state.currenciesList = action.payload?.map((item) => item);
    },
    setReportsBaseCurrency: (state, action: PayloadAction<Currency>) => {
      state.baseCurrency = action.payload;
    },
    clearAccountingJobs: (state) => {
      state.accountingJobs = undefined;
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      reportsDictionariesApi.endpoints.getReportsCurrencies.matchFulfilled,
      (state, action: PayloadAction<Response<Currency>>) => {
        state.currencies = action.payload.data.edges.map((item) => ({
          name: item.node.name,
          value: item.node.id,
        }));

        state.currenciesList = action.payload.data.edges?.map(
          (item) => item.node,
        );
      },
    );
    builder.addMatcher(
      reportsDictionariesApi.endpoints.getAccountingJobsInProgress.matchPending,
      (state) => {
        state.accountingJobs = undefined;
      },
    );
    builder.addMatcher(
      reportsDictionariesApi.endpoints.getAccountingJobsInProgress
        .matchFulfilled,
      (state, action: PayloadAction<Response<AccountingJob>>) => {
        state.accountingJobs = action.payload.data.edges.map(
          (item) => item.node,
        );
      },
    );
    builder.addMatcher(
      reportsDictionariesApi.endpoints.getAccountingJobsInProgressStates
        .matchFulfilled,
      (state, action: PayloadAction<Response<AccountingJob>>) => {
        state.accountingJobs = action.payload.data.edges.map(
          (item) => item.node,
        );
      },
    );
    builder.addMatcher(
      reportsDictionariesApi.endpoints.getReportsBaseCurrency.matchFulfilled,
      (state, action: PayloadAction<{ data: Currency }>) => {
        state.baseCurrency = action.payload.data;
      },
    );
    builder.addMatcher(
      reportsDictionariesApi.endpoints.getReportsDictionariesAccountsCategories
        .matchFulfilled,
      (state, action: PayloadAction<Response<Category>>) => {
        state.accountsCategories = action.payload.data?.edges?.map(
          (item) => item.node,
        );
      },
    );
    builder
      .addMatcher(
        reportsDictionariesApi.endpoints.getReportsPermissions.matchFulfilled,
        (state, action: PayloadAction<{ data: PermissionResponse }>) => {
          state.activePermissions = action.payload.data.permissions;

          localStorage.setItem(
            'activePermissions',
            JSON.stringify(action.payload.data.permissions),
          );
        },
      )
      .addMatcher(
        reportsDictionariesApi.endpoints.getReportsWhitelabel.matchFulfilled,
        (
          state,
          action: PayloadAction<{
            data: {
              data: {
                value: Record<string, string>;
              };
            };
          }>,
        ) => {
          state.whitelabel = action.payload.data;
        },
      );
  },
});

export const {
  setActivePermissions,
  setReportsBaseCurrency,
  setReportsCurrencies,
  clearAccountingJobs,
} = reportsDictionariesSlice.actions;

export default reportsDictionariesSlice.reducer;
